import React, { useState, useEffect } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

interface SliderProps {
  value?: number[];
  defaultValue?: number[];
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange?: (value: number) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Slider component wrapping Radix UI Slider.
 * Displays a range input with value and unit display.
 *
 * Used throughout Pattern.tsx for controlling generative parameters:
 * - Range, Spread, Space, Dissonance
 */
export const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      value,
      defaultValue = [0],
      min,
      max,
      step = 1,
      unit = "",
      onChange,
      disabled,
      className,
    },
    forwardedRef
  ) => {
    // Track current value for display during drag
    const [currentValue, setCurrentValue] = useState<number[]>(value || defaultValue);

    // Sync internal state when controlled value changes (e.g., from scrambling)
    useEffect(() => {
      if (value !== undefined) {
        setCurrentValue(value);
      }
    }, [value]);

    const handleValueChange = (newValue: number[]) => {
      // Update display immediately while dragging
      setCurrentValue(newValue);
      // Call parent onChange callback
      onChange?.(newValue[0]);
    };

    return (
      <div className="w-full space-y-2">
        <SliderPrimitive.Slider
          ref={forwardedRef}
          value={value}
          defaultValue={defaultValue}
          min={min}
          max={max}
          step={step}
          onValueChange={handleValueChange}
          disabled={disabled}
          className={`relative flex w-full touch-none select-none items-center ${className || ""}`}
        >
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-gray-800">
            <SliderPrimitive.Range className="absolute h-full bg-gray-500" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-gray-500 bg-gray-300 shadow-lg transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-50" />
        </SliderPrimitive.Slider>
        <div className="flex justify-end">
          <output className="text-sm text-gray-400 font-mono">
            {currentValue[0]}
            {unit}
          </output>
        </div>
      </div>
    );
  }
);

Slider.displayName = "Slider";

export default Slider;